import type {
  AgentConversation,
  AgentMessage,
  AgentRun,
} from '@this+that/models';
import log from 'loglevel';
import type { StateCreator } from 'zustand';

import type { AgentConversationsStore, ConversationSlot } from './types';

const OPTIMISTIC_KEY_PREFIX = 'OPTIMISTIC_' as const;
const SEEN_SYSTEM_AT_STORAGE_KEY =
  'agent-conversations-seen-system-at' as const;

function cloneSlots(
  slots: Map<string, ConversationSlot>,
): Map<string, ConversationSlot> {
  return new Map(slots);
}

function buildUpdatedSlot(
  existing: ConversationSlot,
  patch: Partial<ConversationSlot>,
): ConversationSlot {
  return { ...existing, ...patch };
}

function sweepOptimistic(messages: AgentMessage[]): AgentMessage[] {
  return messages.filter(
    (m) => !m.convMsgIdx.startsWith(OPTIMISTIC_KEY_PREFIX),
  );
}

function upsertIntoMessages(
  messages: AgentMessage[],
  incoming: AgentMessage,
): AgentMessage[] {
  const idx = messages.findIndex((m) => m.convMsgIdx === incoming.convMsgIdx);
  if (idx === -1) {
    return [...messages, incoming].sort((a, b) =>
      a.convMsgIdx < b.convMsgIdx ? -1 : a.convMsgIdx > b.convMsgIdx ? 1 : 0,
    );
  }
  const next = [...messages];
  next[idx] = incoming;
  return next;
}

function isNewerRun(incoming: AgentRun, current: AgentRun | null): boolean {
  if (!current) {
    return true;
  }
  return (
    incoming.runId === current.runId || incoming.createdAt > current.createdAt
  );
}

function isConversationEqual(
  a: AgentConversation,
  b: AgentConversation,
): boolean {
  return (
    a.conversationId === b.conversationId &&
    a.title === b.title &&
    a.archivedAt === b.archivedAt &&
    a.lastMessageAt === b.lastMessageAt &&
    a.messageCount === b.messageCount &&
    a.lastRunId === b.lastRunId &&
    (a.pendingUserAction === true) === (b.pendingUserAction === true)
  );
}

function isPendingConversation(conversation: AgentConversation): boolean {
  return (
    conversation.pendingUserAction === true &&
    (conversation.archivedAt === null || conversation.archivedAt === undefined)
  );
}

type PendingIdsState = Pick<
  AgentConversationsStore,
  'pendingUserActionIds' | 'pendingIdsLoadChanges'
>;

// Single writer of the id Set: every change is also recorded while the ids are being loaded, so the result can be re-based on them.
function patchPendingIds(
  state: PendingIdsState,
  changes: [string, boolean][],
): Partial<AgentConversationsStore> | null {
  let ids = state.pendingUserActionIds;
  for (const [id, pending] of changes) {
    state.pendingIdsLoadChanges?.set(id, pending);
    if (ids.has(id) === pending) {
      continue;
    }
    if (ids === state.pendingUserActionIds) {
      ids = new Set(ids);
    }
    if (pending) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
  }
  return ids === state.pendingUserActionIds
    ? null
    : { pendingUserActionIds: ids };
}

function pendingChangesFor(
  conversations: AgentConversation[],
): [string, boolean][] {
  return conversations.map((c) => [c.conversationId, isPendingConversation(c)]);
}

function isActiveRunEqual(a: AgentRun | null, b: AgentRun | null): boolean {
  if (a === null && b === null) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return (
    a.runId === b.runId &&
    a.status === b.status &&
    a.updatedAt === b.updatedAt &&
    a.userStatusCreatedAt === b.userStatusCreatedAt
  );
}

function areMessagesEquivalent(a: AgentMessage[], b: AgentMessage[]): boolean {
  if (a.length === 0 && b.length === 0) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return (
    a[0].convMsgIdx === b[0].convMsgIdx &&
    a[a.length - 1].convMsgIdx === b[b.length - 1].convMsgIdx
  );
}

function isSlotEquivalent(
  existing: ConversationSlot,
  incoming: ConversationSlot,
): boolean {
  return (
    isConversationEqual(existing.conversation, incoming.conversation) &&
    isActiveRunEqual(existing.activeRun, incoming.activeRun) &&
    existing.messagesLoaded === incoming.messagesLoaded &&
    areMessagesEquivalent(existing.messages, incoming.messages)
  );
}

function readSeenSystemAt(): Record<string, number> {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(SEEN_SYSTEM_AT_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'number') {
        result[k] = v;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeSeenSystemAt(map: Record<string, number>): void {
  try {
    localStorage.setItem(SEEN_SYSTEM_AT_STORAGE_KEY, JSON.stringify(map));
  } catch (err) {
    log.error('[agent-conversations] failed to persist seenSystemAtById', err);
  }
}

function mergeAndPersistSeenAt(
  current: Record<string, number>,
  entries: { id: string; lastMessageAt: number }[],
): Record<string, number> | null {
  let changed = false;
  const next = { ...current };
  for (const { id, lastMessageAt } of entries) {
    const existing = next[id] ?? 0;
    if (lastMessageAt > existing) {
      next[id] = lastMessageAt;
      changed = true;
    }
  }
  if (!changed) {
    return null;
  }
  writeSeenSystemAt(next);
  return next;
}

function isDockListUnchanged(
  items: { conversation: AgentConversation; activeRun: AgentRun | null }[],
  slots: Map<string, ConversationSlot>,
): boolean {
  if (items.length !== slots.size) {
    return false;
  }
  for (const item of items) {
    const id = item.conversation.conversationId;
    const existing = slots.get(id);
    if (existing === undefined) {
      return false;
    }
    if (!isConversationEqual(item.conversation, existing.conversation)) {
      return false;
    }
    if (!isActiveRunEqual(item.activeRun, existing.activeRun)) {
      return false;
    }
  }
  return true;
}

export const createAgentConversationsSlice: StateCreator<
  AgentConversationsStore
> = (set, get) => ({
  slots: new Map(),
  focusedId: null,
  dockListLoaded: false,
  acknowledgedAttentionIds: new Set(),
  seenSystemAtById: readSeenSystemAt(),
  pendingUserActionIds: new Set(),
  pendingIdsLoadChanges: null,

  setDockList: (items) => {
    const { slots, dockListLoaded } = get();
    if (dockListLoaded && isDockListUnchanged(items, slots)) {
      return;
    }
    const next = cloneSlots(slots);
    for (const item of items) {
      const id = item.conversation.conversationId;
      const existing = next.get(id);
      if (existing !== undefined) {
        next.set(
          id,
          buildUpdatedSlot(existing, {
            conversation: item.conversation,
            activeRun: item.activeRun,
          }),
        );
      } else {
        next.set(id, {
          conversation: item.conversation,
          activeRun: item.activeRun,
          messages: [],
          messagesLoaded: false,
        });
      }
    }
    set({
      slots: next,
      dockListLoaded: true,
      ...patchPendingIds(
        get(),
        pendingChangesFor(items.map((item) => item.conversation)),
      ),
    });
  },

  upsertSlot: (slot) => {
    const existing = get().slots.get(slot.conversation.conversationId);
    if (existing !== undefined && isSlotEquivalent(existing, slot)) {
      return;
    }
    const next = cloneSlots(get().slots);
    next.set(slot.conversation.conversationId, slot);
    set({
      slots: next,
      ...patchPendingIds(get(), pendingChangesFor([slot.conversation])),
    });
  },

  setFocusedId: (id) => {
    if (get().focusedId === id) {
      return;
    }
    const { slots, seenSystemAtById } = get();
    const slot = id !== null ? slots.get(id) : undefined;
    if (
      id !== null &&
      slot !== undefined &&
      slot.conversation.isSystem === true
    ) {
      const merged = mergeAndPersistSeenAt(seenSystemAtById, [
        { id, lastMessageAt: slot.conversation.lastMessageAt },
      ]);
      if (merged !== null) {
        set({ focusedId: id, seenSystemAtById: merged });
        return;
      }
    }
    set({ focusedId: id });
  },

  updateActiveRun: (conversationId, run) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    if (!isNewerRun(run, existing.activeRun)) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(conversationId, buildUpdatedSlot(existing, { activeRun: run }));
    set({ slots: next });
  },

  upsertMessage: (conversationId, message) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        messages: upsertIntoMessages(existing.messages, message),
      }),
    );
    set({ slots: next });
  },

  sweepOptimisticMessages: (conversationId) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        messages: sweepOptimistic(existing.messages),
      }),
    );
    set({ slots: next });
  },

  removeSlot: (conversationId) => {
    const next = cloneSlots(get().slots);
    next.delete(conversationId);
    const focusedId =
      get().focusedId === conversationId ? null : get().focusedId;
    set({
      slots: next,
      focusedId,
      ...patchPendingIds(get(), [[conversationId, false]]),
    });
  },

  markConversationArchived: (conversationId, archivedAt) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        conversation: { ...existing.conversation, archivedAt },
      }),
    );
    set({
      slots: next,
      ...patchPendingIds(get(), [[conversationId, false]]),
    });
  },

  markConversationReopened: (conversationId) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        conversation: { ...existing.conversation, archivedAt: null },
      }),
    );
    set({
      slots: next,
      ...patchPendingIds(get(), [
        [conversationId, existing.conversation.pendingUserAction === true],
      ]),
    });
  },

  updateConversationTitle: (conversationId, title) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      return;
    }
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        conversation: { ...existing.conversation, title },
      }),
    );
    set({ slots: next });
  },

  acknowledgeAttention: (conversationIds) => {
    if (conversationIds.length === 0) {
      return;
    }
    const current = get().acknowledgedAttentionIds;
    const next = new Set(current);
    for (const id of conversationIds) {
      next.add(id);
    }
    set({ acknowledgedAttentionIds: next });
  },

  beginPendingUserActionIdsLoad: () => {
    set({ pendingIdsLoadChanges: new Map() });
  },

  setPendingUserActionIds: (conversationIds) => {
    const { pendingIdsLoadChanges } = get();
    const ids = new Set(conversationIds);
    for (const [id, pending] of pendingIdsLoadChanges ?? []) {
      if (pending) {
        ids.add(id);
      } else {
        ids.delete(id);
      }
    }
    set({ pendingUserActionIds: ids, pendingIdsLoadChanges: null });
  },

  setConversationPendingUserAction: (conversationId, pending) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    const patch: Partial<AgentConversationsStore> = {
      ...patchPendingIds(get(), [[conversationId, pending]]),
    };
    if (
      existing !== undefined &&
      (existing.conversation.pendingUserAction === true) !== pending
    ) {
      const nextSlots = cloneSlots(slots);
      nextSlots.set(
        conversationId,
        buildUpdatedSlot(existing, {
          conversation: {
            ...existing.conversation,
            pendingUserAction: pending,
          },
        }),
      );
      patch.slots = nextSlots;
    }
    if (patch.slots !== undefined || patch.pendingUserActionIds !== undefined) {
      set(patch);
    }
  },

  markSystemConversationsSeen: (entries) => {
    if (entries.length === 0) {
      return;
    }
    const merged = mergeAndPersistSeenAt(get().seenSystemAtById, entries);
    if (merged === null) {
      return;
    }
    set({ seenSystemAtById: merged });
  },

  clearConversationMessages: (conversationId) => {
    const slots = get().slots;
    const existing = slots.get(conversationId);
    if (existing === undefined) {
      log.warn('[clear-debug] store: no slot for conversationId', {
        conversationId,
        knownSlots: Array.from(slots.keys()),
      });
      return;
    }
    log.warn('[clear-debug] store: clearing slot', {
      conversationId,
      messagesBefore: existing.messages.length,
      messagesLoaded: existing.messagesLoaded,
    });
    const next = cloneSlots(slots);
    next.set(
      conversationId,
      buildUpdatedSlot(existing, {
        messages: [],
        activeRun: null,
        messagesLoaded: true,
      }),
    );
    set({ slots: next });
    setTimeout(() => {
      const after = get().slots.get(conversationId);
      log.warn('[clear-debug] store: slot 1s after clear', {
        conversationId,
        messagesAfter: after?.messages.length,
        activeRun: after?.activeRun?.runId ?? null,
      });
    }, 1000);
  },
});
